// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Module Guide lookup — the typed absence (idea-catalog #79).
 *
 * `GET /onboarding/module-tips/:moduleId` used to answer a miss with a bare
 * `null` (a 404 carrying nothing else), so two different facts were one
 * answer:
 *
 *  - the id names no module the platform registers — a page that genuinely has
 *    no module, whose guide text the page itself supplies; and
 *  - the id IS a registered module and nobody wrote its guide — a content
 *    defect, which rendered as a card nobody could tell from a complete one,
 *    or as nothing at all.
 *
 * The miss now says which. "Registered" is decided against `ALL_MODULE_IDS`
 * (`module-config.ts`) — the platform's own module registry, the set packaging,
 * tiering and module toggles read — never against a hand list, and never
 * against the tip records themselves (that comparison could only ever answer
 * "not registered").
 */
import { ALL_MODULE_IDS } from './module-config.js';
/** Why a Module Guide lookup found no record. Closed vocabulary. */
export const MODULE_TIP_ABSENCE_REASONS = [
    /** The id is in no platform module registry; a page may supply its own summary. */
    'module_not_registered',
    /** The id is a registered module with no guide record — a content defect. */
    'no_record_for_registered_module',
];
export function isModuleTipAbsenceReason(value) {
    return (typeof value === 'string' && MODULE_TIP_ABSENCE_REASONS.includes(value));
}
export function isModuleTipAbsence(value) {
    return (typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        isModuleTipAbsenceReason(value.absent));
}
const REGISTERED_MODULE_IDS = new Set(ALL_MODULE_IDS);
/**
 * The absence a lookup for `moduleId` reports when no record exists for it.
 *
 * `registered` defaults to the platform module registry; it is a parameter only
 * so a test can hold the vocabulary still while the registry moves.
 */
export function classifyModuleTipAbsence(moduleId, registered = REGISTERED_MODULE_IDS) {
    return {
        absent: registered.has(moduleId) ? 'no_record_for_registered_module' : 'module_not_registered',
    };
}
//# sourceMappingURL=module-tips.js.map